import React from 'react';
import { useParams } from 'react-router-dom';
import MainContainer from '../Components/MainContainer';
import ErrorModal from '../Components/Modal/ErrorModal';
import Modal from '../Components/Modal/Modal';
import { ApiInstance, ApiRoutes } from '../Utils/ApiInstance';

function Receipt() {
  const { receiptId, uuid } = useParams();
  const [isNotFound, setIsNotFound] = React.useState(false);
  const [data, setData] = React.useState({});
  const [showModal, setShowModal] = React.useState(false);
  const [modalCode, setModalCode] = React.useState(0);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    setIsLoading(true);
    getReceipt();
  }, []);

  const getReceipt = async () => {
    try {
      const response = await ApiInstance.get(`${ApiRoutes.GET_RECEIPT}/${receiptId}/${uuid}`);
      setData(response.data);
    } catch (error) {
      if (error.status === 404) {
        setIsNotFound(true);
      } else if (error.response?.data?.message != undefined) {
        setErrorMessage(error.response.data.message);
        setModalCode(2);
        setShowModal(true);
      } else {
        setErrorMessage(error.message);
        setModalCode(2);
        setShowModal(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = e => {
    e.preventDefault();
    setShowModal(false);
    setModalCode(0);
    setErrorMessage('');
  };

  const generateModalContent = () => {
    if (modalCode === 2) {
      return <ErrorModal description={errorMessage} />;
    }
  };

  // Check if this is a DOPU order (uses tokens instead of RM)
  const isDopu = data?.type?.toLowerCase?.() === 'dopu';
  const paymentSummary = Array.isArray(data?.payment_summary) ? data.payment_summary : [];
  const paymentSummarySubtotal = paymentSummary.reduce((sum, item) => sum + parseFloat(item?.total || 0), 0);
  const displaySubtotal = isDopu && paymentSummary.length > 0
    ? paymentSummarySubtotal
    : parseFloat(data?.subtotal || 0);

  // Format currency display - tokens for DOPU, RM for others
  // Tokens render exactly as the mobile app and admin show them: up to 2 dp with
  // trailing zeros trimmed (1.5 / 10.5 / 60), never "1.50 Tokens".
  const formatCurrency = (amount) => {
    const num = parseFloat(amount || 0);
    if (isDopu) {
      const rounded = Math.round(num * 100) / 100;
      const text = Number.isInteger(rounded)
        ? String(rounded)
        : rounded.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
      return `${text} Tokens`;
    }
    return `RM ${num.toFixed(2)}`;
  };

  // Format payment method for display
  const formatPaymentMethod = (method) => {
    if (!method) return '-';
    const labels = {
      'token_payment': 'Token Payment',
      'online_payment': 'Online Payment',
      'cash': 'Cash',
      'card': 'Card',
      'ewallet': 'E-Wallet',
      'fpx': 'FPX',
    };
    return labels[method?.toLowerCase()] || method.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Get compact voucher description
  const getVoucherDescription = (voucher) => {
    if (!voucher) return null;
    const { reward_type } = voucher;

    // Modern discount type
    if (reward_type === 'discount' && voucher.discount_type) {
      const isPercentage = voucher.discount_type === 'percentage';
      let desc = isPercentage 
        ? `${voucher.discount_value}% off` 
        : `RM${parseFloat(voucher.discount_value || 0).toFixed(2)} off`;
      if (voucher.max_discount_amount) {
        desc += ` (max RM${parseFloat(voucher.max_discount_amount).toFixed(2)})`;
      }
      return desc;
    }

    // Legacy percentage discount
    if (reward_type === 'percentage_discount') {
      return `${voucher.value}% off`;
    }

    // Legacy fixed discount
    if (reward_type === 'fixed_discount') {
      return `RM${parseFloat(voucher.value || 0).toFixed(2)} off`;
    }

    // Free service voucher
    if (reward_type === 'free_service') {
      const serviceLabels = { free_wash: 'Free Wash', free_dry: 'Free Dry' };
      return serviceLabels[voucher.service_type] || 'Free Service';
    }

    // Currency/Token voucher
    if (reward_type === 'currency') {
      const parts = [];
      if (voucher.tokens) parts.push(`${voucher.tokens} Tokens`);
      if (voucher.points) parts.push(`${voucher.points} Points`);
      return parts.length > 0 ? parts.join(' + ') : 'Currency Reward';
    }

    // Physical item voucher
    if (reward_type === 'physical_item') {
      return voucher.item_name || 'Physical Item';
    }

    return 'Voucher Applied';
  };

  if (isLoading) {
    return (
      <div className="w-full min-h-screen flex flex-col justify-center items-center bg-gradient-to-b from-blue-50 to-gray-100 px-4">
        <div className="relative flex flex-col items-center">
          {/* Spinning ring */}
          <div className="absolute w-24 h-24 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
          {/* Dobiqueen logo in center */}
          <div className="w-24 h-24 flex items-center justify-center">
            <img 
              src="/images/webclip.png" 
              alt="Dobiqueen" 
              className="w-14 h-14 rounded-full animate-pulse"
            />
          </div>
        </div>
        <p className="mt-6 text-gray-500 text-sm animate-pulse">Loading receipt...</p>
      </div>
    );
  }

  if (isNotFound) {
    return (
      <div className="w-full min-h-screen flex flex-col justify-center items-center bg-gray-50 px-4 py-8">
        <img
          src="/images/dq-logo-text-color-hr.svg"
          alt="Dobiqueen"
          className="w-48 sm:w-64 max-w-full mb-6"
        />
        <div className="bg-white rounded-xl shadow-sm p-6 sm:p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Receipt Not Found</h2>
          <p className="text-gray-600 text-sm sm:text-base">
            We're sorry, but we couldn't find the receipt you were looking for. 
            Please double-check the link, or contact our support team for assistance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <MainContainer title={'Receipt'}>
      <div className="bg-gradient-to-b from-blue-50 to-gray-100 min-h-screen">
        <div className="w-full max-w-screen-md mx-auto py-3 sm:py-4 px-3 sm:px-4">
          
          {/* Compact Header with Logo + Receipt Info + Customer Details */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-3">
            {/* Top row: Logo and Receipt number */}
            <div className="flex justify-between items-center pb-3 border-b border-gray-100">
              <img className="w-28 sm:w-32 h-auto" src="/images/img1.png" alt="Dobiqueen" />
              <div className="text-right">
                <p className="text-xs text-gray-400 uppercase">Receipt</p>
                <p className="text-xs font-mono text-gray-600">{data?.receipt ?? '-'}</p>
              </div>
            </div>
            
            {/* Transaction details - compact grid */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-3 text-xs">
              <div>
                <span className="text-gray-400">Customer</span>
                <p className="font-medium text-gray-800 truncate">{data?.customer_name ?? '-'}</p>
              </div>
              <div>
                <span className="text-gray-400">Phone</span>
                <p className="font-medium text-gray-800">{data?.customer_phone ?? '-'}</p>
              </div>
              <div>
                <span className="text-gray-400">Date</span>
                <p className="font-medium text-gray-800">{data?.date_time ?? '-'}</p>
              </div>
              <div>
                <span className="text-gray-400">Channel</span>
                <p className="font-medium text-gray-800">{data?.channel ?? '-'}</p>
              </div>
              {data?.outlet_name && (
                <div className="col-span-2">
                  <span className="text-gray-400">Outlet</span>
                  <p className="font-medium text-gray-800 truncate">
                    {data.outlet_name}
                    {data.outlet_id && <span className="text-gray-400 ml-1">({data.outlet_id})</span>}
                  </p>
                </div>
              )}
              <div>
                <span className="text-gray-400">Order ID</span>
                <p className="font-medium text-gray-800 font-mono">{data?.order_id ?? '-'}</p>
              </div>
              <div>
                <span className="text-gray-400">Txn ID</span>
                <p className="font-medium text-gray-800 font-mono truncate">{data?.transaction_id ?? '-'}</p>
              </div>
            </div>
          </div>

          {/* Payment Summary Card */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-3">

            {/* Items - Mobile Card View */}
            <div className="block sm:hidden space-y-2">
              {paymentSummary.length > 0 ? (
                paymentSummary.map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-2.5">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0 pr-2">
                        <p className="font-medium text-gray-800 text-sm">{item.code}</p>
                        <p 
                          className="text-xs text-gray-500 line-clamp-1"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      </div>
                      <span className="font-semibold text-gray-800 text-sm whitespace-nowrap">
                        {formatCurrency(item.total)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 mt-1.5 pt-1.5 border-t border-gray-200">
                      <span>Qty: {item.quantity}</span>
                      <span>@ {formatCurrency(item.unit_price)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  {data?.type === 'unknown' ? 'Details not available' : 'No items'}
                </div>
              )}
            </div>

            {/* Items - Desktop Table View */}
            <div className="hidden sm:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left py-2 px-3 font-semibold text-gray-600 rounded-l-lg">Item</th>
                    <th className="text-center py-2 px-2 font-semibold text-gray-600 w-12">Qty</th>
                    <th className="text-right py-2 px-2 font-semibold text-gray-600 w-24">Price</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-600 w-24 rounded-r-lg">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentSummary.length > 0 ? (
                    paymentSummary.map((item, i) => (
                      <tr key={i} className="border-b border-gray-50">
                        <td className="py-2 px-3">
                          <div className="font-medium text-gray-800">{item.code}</div>
                          <div
                            className="text-xs text-gray-500 max-w-xs truncate"
                            dangerouslySetInnerHTML={{ __html: item.description }}
                          />
                        </td>
                        <td className="py-2 px-2 text-center text-gray-600">{item.quantity}</td>
                        <td className="py-2 px-2 text-right text-gray-600 whitespace-nowrap">{formatCurrency(item.unit_price)}</td>
                        <td className="py-2 px-3 text-right font-medium text-gray-800 whitespace-nowrap">{formatCurrency(item.total)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="py-6 text-center text-gray-500">
                        {data?.type === 'unknown' ? 'Details not available' : 'No items'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals Section */}
            <div className="border-t border-gray-200 mt-3 pt-3 space-y-1 text-sm">
              {/* Items Subtotal */}
              <div className="flex justify-between">
                <span className="text-gray-500">Items Subtotal</span>
                <span className="text-gray-800">{formatCurrency(displaySubtotal)}</span>
              </div>

              {/* Shipping - Show for order-based types */}
              {['order', 'pud', 'business', 'hotel', 'venue', 'dopu'].includes(data?.type) && parseFloat(data?.shipping_amount || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Shipping</span>
                  <span className="text-gray-800">{formatCurrency(data?.shipping_amount)}</span>
                </div>
              )}

              {/* Overload Fee */}
              {parseFloat(data?.over_load_amount || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Overload</span>
                  <span className="text-gray-800">{formatCurrency(data?.over_load_amount)}</span>
                </div>
              )}

              {/* Order Subtotal - Show for free orders OR when there's discount */}
              {(() => {
                const isFreeOrder = data?.payment_method?.toLowerCase() === 'free' && parseFloat(data?.total || 0) === 0;
                const hasDiscount = parseFloat(data?.discount_amount || 0) > 0;
                const hasShipping = parseFloat(data?.shipping_amount || 0) > 0;
                const orderSubtotal = displaySubtotal + parseFloat(data?.shipping_amount || 0) + parseFloat(data?.over_load_amount || 0);
                
                if (['order', 'pud', 'business', 'hotel', 'venue'].includes(data?.type) && (hasDiscount || isFreeOrder) && hasShipping) {
                  return (
                    <div className="flex justify-between pt-1 border-t border-gray-100">
                      <span className="text-gray-600 font-medium">Order Subtotal</span>
                      <span className="text-gray-800 font-medium">
                        {formatCurrency(orderSubtotal)}
                      </span>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Discount - show for voucher discount OR free order */}
              {(() => {
                const isFreeOrder = data?.payment_method?.toLowerCase() === 'free' && parseFloat(data?.total || 0) === 0;
                const hasDiscount = parseFloat(data?.discount_amount || 0) > 0;
                const orderSubtotal = displaySubtotal + parseFloat(data?.shipping_amount || 0) + parseFloat(data?.over_load_amount || 0);
                const discountAmount = parseFloat(data?.discount_amount || 0);
                
                // Calculate what percentage they saved
                const getSavingsPercent = () => {
                  if (orderSubtotal > 0 && discountAmount > 0) {
                    return Math.round((discountAmount / orderSubtotal) * 100);
                  }
                  return 0;
                };
                
                // Build discount description from voucher info
                const getDiscountDescription = () => {
                  if (!data?.voucher) return null;
                  const v = data.voucher;
                  if (v.discount_type === 'percentage') {
                    let desc = `${v.discount_value}% off`;
                    if (v.max_discount_amount) {
                      desc += ` (max ${isDopu ? v.max_discount_amount + ' Tokens' : 'RM' + parseFloat(v.max_discount_amount).toFixed(2)})`;
                    }
                    return desc;
                  } else if (v.discount_type === 'fixed') {
                    return isDopu 
                      ? `${v.discount_value} Tokens off`
                      : `RM${parseFloat(v.discount_value).toFixed(2)} off`;
                  }
                  return null;
                };
                
                const discountDesc = data?.voucher?.label || getDiscountDescription();
                const savingsPercent = getSavingsPercent();
                
                if (hasDiscount) {
                  return (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 -mx-4 px-4 py-3 border-y border-green-200">
                      {/* Single row: Voucher code + Original price strikethrough + Discount amount */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-4 h-4 bg-green-500 rounded-full flex-shrink-0">
                            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </span>
                          <div className="min-w-0">
                            <span className="text-green-700 font-semibold text-sm">
                              Voucher {data?.voucher?.code || 'Discount'}
                            </span>
                            <span className="text-gray-400 text-xs ml-2">
                              was <span className="line-through decoration-red-400">{formatCurrency(orderSubtotal)}</span>
                            </span>
                            {discountDesc && (
                              <div className="text-gray-500 text-xs">{discountDesc}</div>
                            )}
                          </div>
                        </div>
                        <span className="text-red-500 font-bold whitespace-nowrap">
                          -{formatCurrency(discountAmount)}
                        </span>
                      </div>
                      
                      {/* Celebratory savings banner */}
                      <div className="flex items-center justify-center gap-2 mt-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg py-1.5 px-3">
                        <span className="text-sm font-bold">
                          🎉 {data?.voucher?.savings_label
                            || (data?.voucher?.discount_type === 'percentage' && savingsPercent > 0
                              ? `${savingsPercent}% OFF`
                              : `You saved ${formatCurrency(discountAmount)}`)}
                        </span>
                      </div>
                    </div>
                  );
                } else if (isFreeOrder && orderSubtotal > 0) {
                  // Free order without voucher - show full amount as discount
                  return (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 -mx-4 px-4 py-3 border-y border-green-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-4 h-4 bg-green-500 rounded-full flex-shrink-0">
                            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </span>
                          <div className="min-w-0">
                            <span className="text-green-700 font-semibold text-sm">Free Order</span>
                            <span className="text-gray-400 text-xs ml-2">
                              <span className="line-through decoration-red-400">{formatCurrency(orderSubtotal)}</span>
                            </span>
                          </div>
                        </div>
                        <span className="text-red-500 font-bold whitespace-nowrap">
                          -{formatCurrency(orderSubtotal)}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-center gap-2 mt-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg py-1.5 px-3">
                        <span className="text-sm font-bold">🎉 100% OFF</span>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Subtotal after the voucher, before rounding — supplied by the API */}
              {data?.after_discount != null && parseFloat(data?.discount_amount || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">After discount</span>
                  <span className="text-gray-800">{formatCurrency(data.after_discount)}</span>
                </div>
              )}

              {/* Rounding */}
              {parseFloat(data?.rounding_adjustment || 0) !== 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">
                    Rounding{isDopu ? ' (tokens are charged in whole units)' : ''}
                  </span>
                  <span className="text-gray-800">
                    {parseFloat(data.rounding_adjustment) > 0 ? '+' : '-'}
                    {formatCurrency(Math.abs(parseFloat(data.rounding_adjustment)))}
                  </span>
                </div>
              )}

              {/* Total Amount */}
              <div className="flex justify-between text-base font-bold pt-2 mt-1 border-t border-gray-200">
                <span className="text-gray-800">
                  {isDopu ? 'Total charged' : 'Total'}
                  {isDopu && <span className="block text-xs font-normal text-gray-500">tokens deducted from your balance</span>}
                </span>
                <span className="text-blue-600">{formatCurrency(data?.total)}</span>
              </div>

              {/* Payment Method - inline */}
              <div className="flex justify-between text-xs pt-2 border-t border-gray-100">
                <span className="text-gray-400">Paid via</span>
                <span className="text-gray-600">{formatPaymentMethod(data?.payment_method)}</span>
              </div>
            </div>
          </div>

          {/* Compact Disclaimer */}
          <div className="text-center py-3 text-xs text-gray-400 space-y-1">
            <p>All transactions are non-transferable & non-refundable.</p>
            <p>E-Invoice available within 3 days @ <a href="https://dobiqueen.my" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">dobiqueen.my</a></p>
          </div>
        </div>
      </div>

      <Modal
        showModal={showModal}
        handleCloseModal={e => handleCloseModal(e)}
        content={generateModalContent()}
        button={
          <a
            onClick={e => handleCloseModal(e)}
            className="x-btn for-secondary is-close-modal w-button"
          >
            Dismiss
          </a>
        }
      />
    </MainContainer>
  );
}

export default Receipt;
